import { GridBaseDataProvider } from '../../../grid/GridBaseDataProvider';
import { Column } from '@nirvana/shared/dtos/types/shared/filters';
import {
  GetRowsRequest,
  GetSummaryRequest,
  GetSummaryResponse,
  SetFiltersRequest,
  UpdateSettingsRequest,
  UpdateSettingsResponse,
} from '../../../grid/types/common';
import { ColumnKeys, COMPLEX_SEARCH_FILTERS } from '../types';
import { convertGridFiltersToExtJsFilters } from '../../../grid/utils/filters';
import { API_TYPE_EDR } from '../../../webcomponents.constants';
import _ from 'lodash';
import { Filter } from '../../../grid/gridFilters/types';
import { ExtJsFilter } from '../../../xdr/responseGrid/types';

export class IncidentsGridDataProvider extends GridBaseDataProvider<Column, ColumnKeys> {
  response;
  dependencies;
  public companyFilterDataProvider = API_TYPE_EDR;

  constructor(dependencies) {
    super();

    this.dependencies = dependencies;
  }

  async getSummary(request: GetSummaryRequest<ColumnKeys>): Promise<GetSummaryResponse> {

    const filters = this.getConvertedFilters(request.filters, request.extraParams);

    let response = this.response;
    if (!response) {
      response = await this.dependencies.api.clients.UnifiedIncidents.readRecords(request, filters).toPromise();
      this.response = response;
    }

    return {
      summary: `${response.total} items`,
      numItems: response.total,
    };
  }

  convertSearchFiltersForCloud(filter: { [x: string]: any }) {
    if (filter['property'] === COMPLEX_SEARCH_FILTERS.DETECTION_RULES_ID && filter['value'] !== '') {
      filter['value'] = [filter['value']];
    }
  }

  convertSearchFiltersForOnPremise(filter: { [x: string]: any }) {
    // For OnPremise, we need special filters, because the search is in mongo
    switch (filter['property']) {
      case COMPLEX_SEARCH_FILTERS.DETECTION_RULES_ID:
        filter['value'] = {
          exactMatch: filter['value'],
        };
        return;
      case COMPLEX_SEARCH_FILTERS.PROCESS_PID:
        // do nothing for PID
        return;
      default:
        // We need to force to search by substring
        if (Object.values(COMPLEX_SEARCH_FILTERS).includes(filter['property']) && filter['value']) {
          filter['value'] = '*' + filter['value'];
        }
        return;
    }
  }

  async getRows(request: GetRowsRequest<Column, ColumnKeys>): Promise<any> {
    let response = this.response;
    if (!response) {
      response = await this.dependencies.api.clients.UnifiedIncidents.readRecords().toPromise();
    } else {
      this.response = null;
    }

    return { rawEvents: response.data };
  }

  public getConvertedFilters(filters: Filter<ColumnKeys>[], extraParams: any = null): Array<ExtJsFilter<ColumnKeys>> {
    for (const filter of filters) {
      if (filter['specifics.lastKillChainPhase']) {
        filter['specifics.lastKillChainPhase']['in'] = filter['specifics.lastKillChainPhase']['in'].map(i =>
          i === 0 ? null : i
        );
      }

      if (filter['specifics.nodesCategories']) {
        filter['specifics.nodesCategories']['in'] = filter['specifics.nodesCategories']['in'].map(i =>
          i === 0 ? null : i
        );
      }

      const complexSearchValue = _.get(filter, ['searchData', 'complexSearch', 'value'], undefined);
      if (complexSearchValue) {
        if (typeof complexSearchValue === 'string' || complexSearchValue instanceof String) {
          filter['searchData']['complexSearch']['value'] = complexSearchValue.trim();
        }
      }
    }
    const extJsFilters: any[] = convertGridFiltersToExtJsFilters(filters);

    if (extraParams) {
      for (const param of extraParams) {
        if (param['insertDemoIncident']) {
          extJsFilters.push({property: 'insertDemoIncident', value: param['insertDemoIncident']})
        }
      }
    }

    for (const filter of extJsFilters) {
      if (!filter.hasOwnProperty('property') || !filter.hasOwnProperty('value')) {
        continue;
      }

      if ((filter['property'] === ColumnKeys.ENTITIES || filter['property'] === ColumnKeys.RESOURCES) &&
          filter['value']) {
        filter['value'] = filter['value'].map((item) => {
          return {
            label: item.label,
            type: item.type
          }
        });
      }

      if (Application.Main.product.isCloud()) {
        this.convertSearchFiltersForCloud(filter);
      } else {
        this.convertSearchFiltersForOnPremise(filter);
      }
    }
    return extJsFilters;
  }

  async updateSettings(params: UpdateSettingsRequest<Column>): Promise<UpdateSettingsResponse> {
    return {};
  }

  async setFilters(request: SetFiltersRequest<ColumnKeys>): Promise<{} | undefined> {
    return {};
  }

  shouldDataReloadOnResetView(): boolean {
    return false;
  }
}
